import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
// Icons
import { IoMdMenu } from 'react-icons/io';
import { GiStoneThrone } from 'react-icons/gi';
// Constants
import { HOME_PAGE_URL } from '../../utils/constants/Routes';
// Nav data
import { getNavLinkItemsArray } from '../../utils/data/NavData';
// Utils
import { removeToken } from '../../utils/user/token';
// Context
import { useUser } from '../../context/UserContext';
// Hooks
import useNavigateToPage from '../../hooks/useNavigateToPage';

function Navbar() {
  const { user, setUser } = useUser();
  const navLinkItemsArray = getNavLinkItemsArray(user);
  const location = useLocation();
  const navigateToPage = useNavigateToPage();

  const [isPhoneNavOpen, setIsPhoneNavOpen] = useState(false);

  const togglePhoneNav = () => {
    setIsPhoneNavOpen((prev) => !prev);
  };

  // If path is "/", only show the "Enter" link to /home
  const filteredNavItems =
    location.pathname === '/'
      ? [{ path: HOME_PAGE_URL, label: 'Enter' }]
      : navLinkItemsArray;

  // Flatten groups like Admin -> subpages
  const flatNavItems = (filteredNavItems ?? [])
    .flatMap((item) => (item?.subpages ? item.subpages : [item]))
    .filter((item) => item && item.path);

  const handleLogout = (event) => {
    event.preventDefault();
    setUser({});
    removeToken();
    navigateToPage(HOME_PAGE_URL, { replace: true });
  };

  return (
    <nav
      role='navigation'
      aria-label='Main Navigation'
      className='relative border-b-2 border-solid border-colour8 h-fit py-4'
    >
      <section className='grid grid-cols-reg px-4 md:px-8'>
        <section className='grid items-center justify-center'>
          <NavLink
            to={HOME_PAGE_URL}
            className={`grid grid-flow-col gap-2 items-center`}
          >
            <div>
              <GiStoneThrone className='text-colour8 text-2xl md:text-3xl' />
            </div>
            <div>
              <p className='uppercase font-bold text-colour1'>
                <span>QUEEN</span>
                <span className='text-colour8 pl-1'>BLAIRE</span>
              </p>
            </div>
          </NavLink>
        </section>

        <section className='grid justify-end'>
          {/* Mobile screen */}
          <button
            aria-label='Toggle navigation menu'
            onClick={togglePhoneNav}
            className='grid lg:hidden w-fit h-fit items-center justify-center text-3xl text-colour1 cursor-pointer'
          >
            <IoMdMenu className='active:scale-90 duration-300' />
          </button>

          {/* Large screen */}
          <ul className='hidden lg:grid grid-flow-col gap-6 items-center text-colour1 '>
            {flatNavItems.map(({ path, label }) => (
              <NavItem key={`${path}::${label}`} url={path} title={label} />
            ))}

            {user?.email && (
              <li>
                <button
                  className='text-xl md:text-lg font-semibold font-poppins hover:brightness-90 duration-200 active:scale-75'
                  onClick={handleLogout}
                >
                  Logout
                </button>
              </li>
            )}
          </ul>
        </section>
      </section>

      {/* Phone navbar */}
      <section
        className={`phone-nav absolute top-full w-full bg-colour2 transition-transform duration-300 ${
          isPhoneNavOpen
            ? 'translate-y-0 opacity-100'
            : '-translate-y-full opacity-0'
        }`}
      >
        <ul className='grid gap-8 items-center justify-center text-center text-colour1 py-10 border-b-2 border-t-2 border-solid border-colour7'>
          {flatNavItems.map(({ path, label }) => (
            <NavItem key={`${path}::${label}`} url={path} title={label} />
          ))}

          {user?.email && (
            <li>
              <button
                className='text-xl md:text-lg font-semibold font-poppins hover:brightness-90 duration-200 active:scale-75'
                onClick={handleLogout}
              >
                Logout
              </button>
            </li>
          )}
        </ul>
      </section>
    </nav>
  );
}

const NavItem = ({ url, title }) => {
  return (
    <li className='active:scale-90'>
      <NavLink
        to={url}
        aria-label={`${title} page navigation tab`}
        className='text-xl md:text-lg hover:brightness-90 duration-200 active:scale-75'
        aria-current={({ isActive }) => (isActive ? 'page' : undefined)}
        style={({ isActive }) => {
          return isActive ? { color: '#660066' } : {};
        }}
      >
        {title}
      </NavLink>
    </li>
  );
};

export default Navbar;
